import { OutputTemplateKind } from "@prisma/client";

/**
 * The mapping between a template kind and its own URL (SC-3554).
 *
 * The kind lives in the path rather than in component state, so a label author can send someone
 * "the documents list" and have them land on it. That makes the URL the single source of truth:
 * there is no local kind to drift out of step with the address bar, and the browser's own back
 * button moves between the two lists for free.
 *
 * Kept as plain TypeScript rather than inline in the page because `jest.config.ts` only matches
 * `.tsx` tests inside `src/ui` — logic embedded in JSX out here cannot be covered at all.
 */

/** Path segment for each kind, relative to the org root. */
const KIND_SEGMENTS = {
  [OutputTemplateKind.LABEL]: "labels",
  [OutputTemplateKind.DOCUMENT]: "documents",
} as const satisfies Record<OutputTemplateKind, string>;

export type OutputTemplateKindSegment = (typeof KIND_SEGMENTS)[OutputTemplateKind];

/**
 * The kind the bare `/output-templates` URL resolves to.
 *
 * Labels rather than documents because labels are the shipping-floor job that runs every day; a
 * document template is authored once a quarter.
 */
export const DEFAULT_OUTPUT_TEMPLATE_KIND: OutputTemplateKind = OutputTemplateKind.LABEL;

/**
 * Customer-facing path, under Settings since SC-3654 — the surface moved to Settings > System and
 * the URL followed, so the address bar agrees with the nav.
 *
 * SINGULAR "custom-output" while the label is plural "Custom Outputs". That is deliberate and
 * Alexander's call, not a typo; flipping it later is this constant plus the redirect below it.
 *
 * "Output template" remains the internal name — the model, the tRPC namespace and this module all
 * keep it. The old `/templates` prefix, where the editor was born in SC-3340, redirects here from
 * `next.config.js` so existing links and bookmarks keep working.
 */
export const OUTPUT_TEMPLATES_BASE_PATH = "/settings/system/custom-output";

/** Where this surface used to live. Kept for the redirect and for anything still keyed on it. */
export const OUTPUT_TEMPLATES_LEGACY_BASE_PATH = "/templates";

/** The list URL for one kind. */
export const outputTemplateListPath = (kind: OutputTemplateKind): string => `${OUTPUT_TEMPLATES_BASE_PATH}/${KIND_SEGMENTS[kind]}`;

/** The editor URL for one template. */
export const outputTemplateEditorPath = (templateId: string): string => `${OUTPUT_TEMPLATES_BASE_PATH}/${templateId}`;

/**
 * The kind a list path names, or null when it names none.
 *
 * Returns null rather than falling back to a default: a caller that cannot tell which list it is
 * on should say so, not quietly render labels while the address bar says something else.
 */
export const outputTemplateKindFromSegment = (segment: string | undefined): OutputTemplateKind | null => {
  const match = Object.entries(KIND_SEGMENTS).find(([, value]) => value === segment);
  // `Object.entries` widens the key to `string`; the lookup above proves it is one of this
  // record's own keys, which the compiler cannot carry through `entries`.
  return match ? (match[0] as OutputTemplateKind) : null;
};

/** Label for the kind switch, in the order the switch shows them. */
export const OUTPUT_TEMPLATE_KIND_OPTIONS: { kind: OutputTemplateKind; label: string }[] = [
  { kind: OutputTemplateKind.LABEL, label: "Labels" },
  { kind: OutputTemplateKind.DOCUMENT, label: "Documents" },
];

/** What a template of this kind is called in prose, for buttons and empty states. */
export const outputTemplateKindNoun = (kind: OutputTemplateKind): string =>
  kind === OutputTemplateKind.LABEL ? "label template" : "document template";
